// Copyright (c) Docugami, Inc. All rights reserved.

import { DgButton } from "components/common/DgCommonStyledComponents";
import { dgFontFamily } from "components/common/Fonts";
import { appContentColor } from "components/shell/presentation/AppStyles";
import { AddDocsPicker, AddDocsPickerOverlay } from "components/storage/AddDocsPicker";
import { ProviderPickerItem } from "components/storage/ProviderPickerItem";
import { useFileCount } from "filemanager/FileCountView";
import { FileUploaderInstance, FileUploadRequest } from "filemanager/FileUploader";
import { isProviderItem, useAddFilesQueue } from "hooks/AddFilesQueue";
import { EmptyDocumentsAddDocsCoachMark } from "hooks/FileViewCoachMarks";
import { ProviderId, UploadDocumentsRequest } from "networking/StorageProviders";
import { useEffect, useMemo, useState } from "react";
import { StorageManagerInstance } from "storage/StorageManager";
import styled from "styled-components";
import { AutomationId } from "utils/Automation";
import { AddDocsListView, ListItem } from "./AddDocsListView";
import { DocumentCountInfo } from "./DocumentCountInfo";
import { ExplorerInfoText, ExplorerTextButton, InfoItemFooter } from "./ExplorerStyles";
import { ExplorerView } from "./ExplorerView";

const TitleText = styled(ExplorerInfoText)`
    font-size: 32px;
`;

const DocQueueHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding-bottom: 10px;
    border-bottom: 1px solid black;
`;

export const ViewContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    height: 100%;
    gap: 20px;
    padding: 35px 0px 35px;
    box-sizing: border-box;
    box-sizing: border-box;
    overflow-y: auto;
`;

const DocQueueFooter = styled(InfoItemFooter)`
    border-top: 1px solid black;
    justify-content: space-between;
    >div {
        border-right: none;
    }
`;

export const AttentionText = styled.span`
    margin-top: auto;
    font-family: ${dgFontFamily};
    font-size: 14px;
    font-weight: 300;
    color: ${appContentColor};
`;

interface AddDocsViewProps {
    hidePicker?: boolean,
    onComplete?: () => void,
    onDismiss: () => void,
    docSetId?: string
}

export const AddDocsView = (props: AddDocsViewProps): JSX.Element => {
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const selectedProviderId = useMemo(() => { return !selectedId ? null : ProviderId[selectedId as keyof typeof ProviderId] }, [selectedId]);
    const fileCount = useFileCount();

    const { queuedItemProps, pickerCommands } = useAddFilesQueue(selectedId, () => { setSelectedId(null); setShowPicker(false) }, props.docSetId);
    const { items, overlays: itemsOverlays, onItemsDeleted, clearState } = queuedItemProps;
    const { localDocsPickerCommands, providerUploadCommand, overlays: pickerOverlays, dropZone } = pickerCommands;

    const [showPicker, setShowPicker] = useState(items.length === 0);

    useEffect(() => {
        if (!selectedProviderId) {
            return;
        }

        providerUploadCommand.execute();
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedProviderId]);

    useEffect(() => {
        if (props.hidePicker) {
            setShowPicker(false);
        }
    }, [props.hidePicker])

    const hasDocs = useMemo(() => {
        const previouslyUploadedDocs = fileCount ?? 0;
        return previouslyUploadedDocs + items.length;
    }, [items, fileCount]);

    function onFinish() {
        upload(items, props.docSetId);
        clearState();
        props.onComplete?.();
    }

    function onCancel() {
        clearState();
        props.onDismiss();
    }

    const footer = (
        <AddDocsViewFooter
            itemsCount={items.length}
            buttons={[
                <ExplorerTextButton key="Cancel" onClick={onCancel}>Cancel</ExplorerTextButton>,
                <DgButton key="Finish" data-automation-id={AutomationId.DocExplorerAddDocsFinish} disabled={items.length === 0} onClick={onFinish}>FINISH</DgButton>
            ]} />
    );

    const header = <AddDocsViewHeader onAddMoreDocs={!showPicker ? () => setShowPicker(true) : undefined} />

    const content = <AddDocsListView items={items} onItemsDeleted={onItemsDeleted} onSyncChanged={() => {}} />

    const docsPickerProps = useMemo(() => ({
        selectedId: selectedId,
        setSelectedId: setSelectedId,
        localDocsUploadCommands: localDocsPickerCommands,
        overlays: pickerOverlays,
        onClose: !!items.length ? () => setShowPicker(false) : undefined
    }), [selectedId, setSelectedId, localDocsPickerCommands, pickerOverlays, items.length])

    const viewOverlays = useMemo(() => showPicker ? [<AddDocsPickerOverlay {...docsPickerProps} />] : [], [showPicker, docsPickerProps])

    const viewContainerProps = {
        onDragOver: dropZone.onDragOver,
        onDrop: dropZone.onDrop,
        onDragEnter: dropZone.onDragEnter
    }

    if (!hasDocs) {
        return (
            <ViewContainer {...viewContainerProps} >
                <div>
                    <EmptyDocumentsAddDocsCoachMark />
                </div>
                <AddDocsPicker {...docsPickerProps} />
                {itemsOverlays}
            </ViewContainer>
        )
    }

    return (
        <>
            <ExplorerView
                header={hasDocs ? header : null}
                content={content}
                overlays={viewOverlays}
                footer={footer}
                telemetryId={'DocsQueue'}
                viewContainerProps={viewContainerProps} />
            {itemsOverlays}
        </>
    )
}

interface AddDocsViewHeaderProps {
    onAddMoreDocs?: () => void;
}

export const AddDocsViewHeader = (props: AddDocsViewHeaderProps): JSX.Element => {
    return (
        <DocQueueHeader>
            <TitleText>Docs To Add</TitleText>
            {!!props.onAddMoreDocs && <DgButton data-automation-id={AutomationId.DocExplorerAddDocsAddMore} onClick={props.onAddMoreDocs}>ADD MORE DOCS</DgButton>}
        </DocQueueHeader>
    )
}

interface AddDocsViewFooterProps {
    itemsCount?: number;
    buttons?: JSX.Element[];
}

const FooterButtonsContainer = styled.div`
    display: flex;
    align-items: baseline;
    gap: 10px;
`;

export const AddDocsViewFooter = (props: AddDocsViewFooterProps): JSX.Element => {
    return (
        <DocQueueFooter>
            <div />
            {props.itemsCount !== undefined && props.itemsCount > 0 && <DocumentCountInfo count={props.itemsCount} countName={'item'} additionalText={' in queue'} />}
            <FooterButtonsContainer>
                {props.buttons}
            </FooterButtonsContainer>
        </DocQueueFooter>
    );
}

function upload(items: ListItem[], docSetId?: string) {
    const localItems: FileUploadRequest[] = [];
    const remoteItems: { [providerId: string]: UploadDocumentsRequest } = {};

    for (const listItem of items) {
        if (isProviderItem(listItem)) {
            const pickerItem: ProviderPickerItem = listItem.id;
            let request = remoteItems[pickerItem.providerId];
            if (!request) {
                request = {
                    providerId: pickerItem.providerId,
                    items: [],
                    docSetId: docSetId
                }

                remoteItems[pickerItem.providerId] = request;
            }

            request.items.push({
                item: pickerItem.item,
                isSynced: !!listItem.autoSync
            });
        }
        else {
            localItems.push(listItem.id);
        }
    }

    FileUploaderInstance.upload(localItems);

    for (const request of Object.values(remoteItems)) {
        StorageManagerInstance.uploadRequest(request);
    }
}
